import React, { useEffect, useState } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc, serverTimestamp, query, where, orderBy } from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './AuthContext';
import { format } from 'date-fns';

export default function Prestamos() {
  const { userData } = useAuth();
  const isAdmin = userData?.role === 'admin';
  const [prestamos, setPrestamos] = useState([]);
  const [stock, setStock] = useState([]);
  const [embajadoras, setEmbajadoras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const emptyForm = { deEmbajadoraId: '', aEmbajadoraId: '', stockId: '', cantidad: 1, observacion: '' };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('activos');

  useEffect(() => { if (userData) load(); }, [userData]);

  async function load() {
    if (!userData?.uid) return;
    setLoading(true);
    try {
      const usersSnap = await getDocs(query(collection(db, 'users'), where('approved', '==', true)));
      const embs = [];
      usersSnap.forEach(d => embs.push({ id: d.id, ...d.data() }));
      setEmbajadoras(embs);

      const stockSnap = await getDocs(collection(db, 'stock'));
      const stockRows = [];
      stockSnap.forEach(d => stockRows.push({ id: d.id, ...d.data() }));
      setStock(stockRows);

      const snap = await getDocs(query(collection(db, 'prestamos'), orderBy('fecha', 'desc')));
      const rows = [];
      snap.forEach(d => rows.push({ id: d.id, ...d.data() }));
      const filtered = isAdmin ? rows : rows.filter(r =>
        r.deEmbajadoraId === userData.uid || r.aEmbajadoraId === userData.uid
      );
      setPrestamos(filtered);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  // Stock disponible de quien presta
  const deStock = stock.filter(s =>
    isAdmin
      ? (form.deEmbajadoraId ? s.embajadoraId === form.deEmbajadoraId && s.stockDisponible > 0 : false)
      : s.embajadoraId === userData?.uid && s.stockDisponible > 0
  );

  async function handlePrestamo(e) {
    e.preventDefault();
    setError('');
    const embId = isAdmin ? form.deEmbajadoraId : userData.uid;
    if (embId === form.aEmbajadoraId) { setError('Las embajadoras deben ser diferentes.'); return; }
    const stk = stock.find(s => s.id === form.stockId);
    if (!stk) { setError('Producto no encontrado'); return; }
    const cant = Number(form.cantidad);
    if (stk.stockDisponible < cant) { setError(`Stock insuficiente. Disponible: ${stk.stockDisponible}`); return; }
    setSaving(true);
    try {
      const deEmb = embajadoras.find(e => e.id === embId) || { name: userData.name };
      const aEmb = embajadoras.find(e => e.id === form.aEmbajadoraId);

      await addDoc(collection(db, 'prestamos'), {
        deEmbajadoraId: embId, deEmbajadoraNombre: deEmb.name,
        aEmbajadoraId: form.aEmbajadoraId, aEmbajadoraNombre: aEmb?.name || '',
        stockId: stk.id, producto: stk.producto, aroma: stk.aroma,
        cantidad: cant, observacion: form.observacion,
        estado: 'activo', fecha: serverTimestamp()
      });

      // Descontar de quien presta
      await updateDoc(doc(db, 'stock', stk.id), {
        egresos: (stk.egresos || 0) + cant,
        stockDisponible: stk.stockDisponible - cant,
        updatedAt: serverTimestamp()
      });

      // Sumar a quien recibe
      const destStk = stock.find(s =>
        s.embajadoraId === form.aEmbajadoraId &&
        s.catalogoId === stk.catalogoId
      );
      if (destStk) {
        await updateDoc(doc(db, 'stock', destStk.id), {
          ingresos: (destStk.ingresos || 0) + cant,
          stockDisponible: destStk.stockDisponible + cant,
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, 'stock'), {
          catalogoId: stk.catalogoId || '',
          producto: stk.producto, aroma: stk.aroma, precio: stk.precio,
          embajadoraId: form.aEmbajadoraId, embajadoraNombre: aEmb?.name || '',
          stockInicial: 0, ingresos: cant, egresos: 0,
          stockDisponible: cant, stockMinimo: stk.stockMinimo || 1,
          prestado: true, createdAt: serverTimestamp()
        });
      }

      // Movimientos
      await addDoc(collection(db, 'movimientos'), {
        tipo: 'prestamo_salida', stockId: stk.id,
        producto: stk.producto, aroma: stk.aroma,
        embajadoraId: embId, embajadoraNombre: deEmb.name,
        cantidad: -cant,
        observacion: `Préstamo a ${aEmb?.name} (x${cant})`,
        fecha: serverTimestamp()
      });

      await load();
      setShowModal(false);
      setForm(emptyForm);
    } catch (err) { setError(err.message); }
    setSaving(false);
  }

  async function handleDevolver(prestamo) {
    if (!window.confirm('¿Confirmar devolución?')) return;
    await updateDoc(doc(db, 'prestamos', prestamo.id), { estado: 'devuelto', fechaDevolucion: serverTimestamp() });

    const stk = stock.find(s => s.id === prestamo.stockId);
    if (stk) {
      await updateDoc(doc(db, 'stock', stk.id), {
        ingresos: (stk.ingresos || 0) + prestamo.cantidad,
        stockDisponible: stk.stockDisponible + prestamo.cantidad,
        updatedAt: serverTimestamp()
      });
    }
    const destStk = stock.find(s =>
      s.embajadoraId === prestamo.aEmbajadoraId &&
      s.producto === prestamo.producto && s.aroma === prestamo.aroma
    );
    if (destStk) {
      await updateDoc(doc(db, 'stock', destStk.id), {
        egresos: (destStk.egresos || 0) + prestamo.cantidad,
        stockDisponible: Math.max(0, destStk.stockDisponible - prestamo.cantidad),
        updatedAt: serverTimestamp()
      });
    }
    await load();
  }

  const displayed = prestamos.filter(p =>
    tab === 'activos' ? p.estado === 'activo' : p.estado === 'devuelto'
  );

  return (
    <div>
      <div className="page-header">
        <h1>Préstamos</h1>
        <p>Transferencias de stock entre embajadoras</p>
      </div>
      <div className="page-body">
        <div className="toolbar">
          <div className="tabs" style={{ marginBottom: 0, borderBottom: 'none' }}>
            <button className={`tab${tab === 'activos' ? ' active' : ''}`} onClick={() => setTab('activos')}>Activos</button>
            <button className={`tab${tab === 'historial' ? ' active' : ''}`} onClick={() => setTab('historial')}>Historial</button>
          </div>
          <button className="btn btn-primary" onClick={() => { setShowModal(true); setError(''); setForm(emptyForm); }}>
            + Nuevo préstamo
          </button>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            {loading ? <div className="spinner" /> : (
              <table>
                <thead>
                  <tr>
                    <th>Fecha</th><th>De</th><th>Para</th>
                    <th>Producto</th><th>Aroma</th><th>Cant.</th><th>Estado</th>
                    {tab === 'activos' && <th>Acción</th>}
                  </tr>
                </thead>
                <tbody>
                  {displayed.length === 0 ? (
                    <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-light)', padding: 40 }}>Sin préstamos</td></tr>
                  ) : displayed.map(p => (
                    <tr key={p.id}>
                      <td>{p.fecha ? format(p.fecha.toDate ? p.fecha.toDate() : new Date(p.fecha), 'dd/MM/yy') : '-'}</td>
                      <td>{p.deEmbajadoraNombre}</td>
                      <td>{p.aEmbajadoraNombre}</td>
                      <td style={{ fontWeight: 500 }}>{p.producto}</td>
                      <td style={{ color: 'var(--text-light)' }}>{p.aroma}</td>
                      <td style={{ fontWeight: 600 }}>{p.cantidad}</td>
                      <td><span className={`badge ${p.estado === 'activo' ? 'badge-yellow' : 'badge-green'}`}>{p.estado === 'activo' ? 'Activo' : 'Devuelto'}</span></td>
                      {tab === 'activos' && (
                        <td>
                          <button className="btn btn-ghost btn-sm" onClick={() => handleDevolver(p)}>Devolver</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Nuevo préstamo</div>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}
            <form className="modal-form" onSubmit={handlePrestamo}>
              {isAdmin && (
                <div className="field-group">
                  <label>Embajadora que presta</label>
                  <select value={form.deEmbajadoraId} onChange={e => setForm(f => ({ ...f, deEmbajadoraId: e.target.value, stockId: '' }))} required>
                    <option value="">Seleccionar...</option>
                    {embajadoras.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
              )}
              <div className="field-group">
                <label>Embajadora que recibe</label>
                <select value={form.aEmbajadoraId} onChange={e => setForm(f => ({ ...f, aEmbajadoraId: e.target.value }))} required>
                  <option value="">Seleccionar...</option>
                  {embajadoras.filter(e => e.id !== (isAdmin ? form.deEmbajadoraId : userData?.uid)).map(e =>
                    <option key={e.id} value={e.id}>{e.name}</option>
                  )}
                </select>
              </div>
              <div className="field-group">
                <label>Producto a prestar</label>
                <select value={form.stockId} onChange={e => setForm(f => ({ ...f, stockId: e.target.value }))} required>
                  <option value="">Seleccionar...</option>
                  {deStock.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.producto} · {s.aroma} (Disponible: {s.stockDisponible})
                    </option>
                  ))}
                </select>
              </div>
              <div className="field-group">
                <label>Cantidad</label>
                <input type="number" min="1" value={form.cantidad} onChange={e => setForm(f => ({ ...f, cantidad: e.target.value }))} required />
              </div>
              <div className="field-group">
                <label>Observación</label>
                <textarea rows={2} value={form.observacion} onChange={e => setForm(f => ({ ...f, observacion: e.target.value }))} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? '...' : 'Registrar préstamo'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}